<template>
  <div class="d-flex flex-column min-vh-100">
    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-8 mt-8">
            <h1 class="text-primary weight-600">Quick Start Medical Necessity</h1>
            <p class="text-grey mt-2">
              Upload a document and enter the details below to run a clinical policy review right away.
            </p>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <v-form ref="formRef" :disabled="isSubmitting" @submit.prevent="handleSubmit">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="patientFirstName"
                      label="Patient First Name"
                      :rules="[rules.required]"
                      :error-messages="networkFieldErrors.patientFirstName"
                      class="mb-2 lh--force-white-background" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="patientLastName"
                      label="Patient Last Name"
                      :rules="[rules.required]"
                      :error-messages="networkFieldErrors.patientLastName"
                      class="mb-2 lh--force-white-background" />
                  </v-col>
                </v-row>

                <TextDateInput
                  v-model="patientDobDate"
                  label="Patient Date of Birth"
                  :rules="[rules.required]"
                  :error-messages="networkFieldErrors.patientDobDate"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-if="manualEntryStart"
                  v-model="patientMrn"
                  label="Patient MRN (optional)"
                  :disabled="networkPatientChosen"
                  :hint="networkPatientChosen ? NETWORK_PATIENT_HINT : ''"
                  persistent-hint
                  class="mb-2 lh--force-white-background" />

                <v-select
                  v-model="referralStatus"
                  label="Initial / Continuation of Therapy"
                  :items="referralStatusItems"
                  item-title="title"
                  item-value="value"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="providerNpi"
                  label="Provider NPI"
                  :rules="[rules.required, rules.npi]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="drugName"
                  label="Drug Name"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <RemoteAutocomplete
                  v-model="diagnosisIcd10Code"
                  :remote-search="searchIcd10Codes"
                  label="Diagnosis Code (ICD-10)"
                  :rules="[rules.required, icd10CodeRule]"
                  class="mb-2 lh--force-white-background" />

                <template v-if="manualEntryStart">
                  <RemoteAutocomplete
                    v-model="payerNameId"
                    :remote-search="onSearchPayer"
                    :allow-free-text="false"
                    label="Insurance Payer"
                    :rules="[payerRequired]"
                    class="mb-2 lh--force-white-background" />
                  <v-row :dense="isPhone">
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="benefitType"
                        label="Benefit Type"
                        :items="BENEFIT_TYPE_ITEMS"
                        :rules="[rules.required]"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="memberId"
                        label="Member ID (optional)"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                  </v-row>
                  <v-select
                    v-if="!carequalityEnabled"
                    v-model="patientGender"
                    label="Gender"
                    :items="genderItems"
                    :rules="[rules.required]"
                    class="mb-2 lh--force-white-background" />
                </template>
                <template v-else>
                  <v-text-field
                    v-model="phoneNumber"
                    label="Phone Number (optional)"
                    type="tel"
                    :rules="[usPhoneRule]"
                    class="mb-2 lh--force-white-background" />

                  <v-text-field
                    v-model="payorName"
                    label="Insurance Payor (optional)"
                    class="mb-4 lh--force-white-background" />
                </template>

                <template v-if="carequalityEnabled">
                  <h3 class="text-primary weight-600 mb-1">Patient Demographics</h3>
                  <p class="text-body-2 text-grey mb-3">
                    Optional to run the review; required to search the Carequality network for the patient's records.
                  </p>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-select
                        v-model="patientGender"
                        label="Gender"
                        :items="genderItems"
                        clearable
                        :rules="manualEntryStart ? [rules.required] : []"
                        :error-messages="networkFieldErrors.patientGender"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                    <v-col cols="12" sm="8">
                      <v-text-field
                        v-model="patientAddress"
                        label="Address"
                        :error-messages="networkFieldErrors.patientAddress"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="5">
                      <v-text-field
                        v-model="patientCity"
                        label="City"
                        :error-messages="networkFieldErrors.patientCity"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-select
                        v-model="patientState"
                        label="State"
                        :items="usaStatesAbbreviationItems"
                        clearable
                        :error-messages="networkFieldErrors.patientState"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                    <v-col cols="6" sm="4">
                      <v-text-field
                        v-model="patientZip"
                        label="ZIP"
                        :rules="[zipCodeFormatRule]"
                        :error-messages="networkFieldErrors.patientZip"
                        class="mb-2 lh--force-white-background" />
                    </v-col>
                  </v-row>
                  <v-btn
                    variant="tonal"
                    color="primary"
                    block
                    size="large"
                    prepend-icon="mdi-lan-connect"
                    class="mt-2 mb-4"
                    :loading="networkSearching"
                    @click="onSearchNetworkRecords">
                    Search Carequality network
                    <v-badge
                      v-if="networkSearchInFlight || networkRecordsFound"
                      :content="networkRecordsFound || ''"
                      :color="networkSearchInFlight ? 'warning' : 'success'"
                      :class="{ 'network-badge--running': networkSearchInFlight }"
                      inline
                      :title="networkSearchInFlight ? 'Search running' : 'Records found on the network'" />
                    <v-badge
                      v-if="networkSelectedIds.length"
                      :content="networkSelectedIds.length"
                      color="accent"
                      inline
                      title="Network records selected for this clinical review" />
                  </v-btn>
                </template>

                <h3 class="text-primary weight-600 mb-2">Upload Documents</h3>
                <div
                  v-for="(doc, index) in supplementalDocs"
                  :key="index"
                  class="doc-drop-zone mb-3 pa-3 rounded-lg d-flex align-center"
                  :class="{
                    'doc-drop-zone--active': dragOverIndex === index && !doc.links.length,
                  }"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(index)"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="handleFileDrop(index, $event)">
                  <input
                    :ref="(el) => (fileInputRefs[index] = el)"
                    type="file"
                    accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleFileSelect(index, $event)" />
                  <v-icon class="mr-3 flex-shrink-0" :color="doc.links.length ? 'success' : 'grey'">
                    {{ doc.links.length ? "mdi-check-circle" : "mdi-file-outline" }}
                  </v-icon>
                  <div class="flex-grow-1" style="min-width: 0">
                    <v-text-field
                      v-model="doc.label"
                      placeholder="No file selected"
                      variant="plain"
                      density="compact"
                      hide-details
                      class="text-body-2" />
                    <div v-if="doc.error" class="text-caption text-error">{{ doc.error }}</div>
                  </div>
                  <v-btn
                    v-if="!doc.links.length"
                    variant="tonal"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-upload"
                    class="ml-2 flex-shrink-0"
                    :loading="doc.uploading"
                    @click="fileInputRefs[index]?.click()">
                    Upload
                  </v-btn>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    color="error"
                    class="ml-1 flex-shrink-0"
                    @click="supplementalDocs.splice(index, 1)" />
                </div>
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-plus"
                  class="mb-4"
                  @click="supplementalDocs.push({ label: null, links: [], file: null, uploading: false, error: null })">
                  Add Document
                </v-btn>

                <v-btn
                  color="primary"
                  block
                  size="large"
                  type="submit"
                  :loading="isSubmitting"
                  :disabled="isSubmitting || !!createdOrderId">
                  Run Medical Necessity Review
                </v-btn>

                <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>

                <v-alert v-if="successMessage" type="success" variant="tonal" class="mt-4">
                  {{ successMessage }}
                </v-alert>

                <v-alert v-if="patientNotes.length" type="warning" variant="tonal" class="mt-4">
                  <div v-for="note in patientNotes" :key="note">{{ note }}</div>
                </v-alert>

                <div v-if="createdOrderId" class="d-flex flex-wrap ga-3 mt-4">
                  <v-btn color="primary" variant="outlined" @click="viewOrder">View Medical Necessity Review</v-btn>
                  <v-btn color="primary" variant="tonal" @click="resetForm">Start Another</v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <NetworkRecordsDialog
      v-if="carequalityEnabled"
      :key="networkSessionKey"
      ref="networkRecordsRef"
      v-model="networkRecordsVisible"
      :patient-id="networkPatientId"
      :trigger-query="startNetworkQuery"
      @selection-changed="networkSelectedIds = $event"
      @state-changed="onNetworkStateChanged" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { intakeService } from "@/services/intakeService";
import {
  BENEFIT_TYPE_ITEMS,
  RequestKey,
  documentsMissing,
  searchIcd10Codes,
  searchPayers,
  startNotes,
  manualEntryStartErrors,
  manualEntryStartService,
} from "@/services/manualEntryStartService";
import { useResponsive } from "@/composables/useResponsive";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { parseUploadError, needsServerConversion, uploadDocumentFile } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
import { localDateToIso } from "@/utils/dateUtils";
import { usPhoneRule, valueInItemsAutocompleteRule, zipCodeFormatRule } from "@/utils/form";
import { isNPIValid } from "@/utils/validators";
import { usaStatesAbbreviationItems } from "@/utils/usaStates";
import TextDateInput from "@/components/TextDateInput.vue";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";
import NetworkRecordsDialog from "@/components/NetworkRecordsDialog.vue";

const NETWORK_FIELD_MESSAGE = "Required for the network search";
const NETWORK_FIELDS_ALERT = "Fill in the highlighted fields to search the Carequality network.";
const NETWORK_IDENTITY_CHANGED = "The patient details changed while the search was starting. Search again.";
const NETWORK_PATIENT_HINT = "The network search chose the patient, so the MRN is not used.";
const DOCUMENTS_NOT_FILED = "The documents did not reach the review. Submit again.";

const router = useRouter();
const requirementsStore = useRequirementsStore();
const { isPhone } = useResponsive();
const formRef = ref(null);
const MAX_PDF_SIZE_BYTES = 30 * 1024 * 1024;
const MAX_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

const patientFirstName = ref("");
const patientLastName = ref("");
const patientDobDate = ref(null);
const referralStatus = ref(null);
const providerNpi = ref("");
const drugName = ref("");
const diagnosisIcd10Code = ref("");
const phoneNumber = ref("");
const payorName = ref("");
const patientGender = ref(null);
const patientAddress = ref("");
const patientCity = ref("");
const patientState = ref(null);
const patientZip = ref("");
const patientMrn = ref("");
const payerNameId = ref("");
const benefitType = ref(null);
const memberId = ref("");

const referralStatusItems = [
  { title: "Initial Referral", value: "initial" },
  { title: "Continuation of Therapy", value: "continuation" },
];
const genderItems = ["Male", "Female"];

const carequalityEnabled = computed(() => requirementsStore.clientConfig?.intake?.carequalityEnabled ?? false);
const manualEntryStart = computed(() => requirementsStore.standaloneStarts.includes("mn"));
const configLoaded = computed(() => requirementsStore.clientConfigIsActiveCompany);

const loadConfig = () => requirementsStore.refreshCurrentUserClientConfig().catch(() => undefined);

onMounted(() => {
  if (!configLoaded.value) loadConfig();
});
const networkRecordsRef = ref(null);
const networkRecordsVisible = ref(false);
const networkPatientId = ref(null);
const networkSessionKey = ref(0);
const networkSearchIdentityKey = ref(null);
const networkSelectedIds = ref([]);
const networkSearchInFlight = ref(false);
const networkRecordsFound = ref(0);
const networkSearching = ref(false);
const networkFieldsRequired = ref(false);
const networkPatientChosen = computed(
  () => carequalityEnabled.value && networkSelectedIds.value.length > 0 && !!networkPatientId.value
);

const supplementalDocs = ref([{ label: null, links: [], file: null, uploading: false, error: null }]);
const fileInputRefs = ref({});
const isSubmitting = ref(false);
const errors = ref([]);
const successMessage = ref(null);
const createdOrderId = ref(null);
const patientNotes = ref([]);
const submitAttempted = ref(false);
const dragOverIndex = ref(null);
const requestKey = new RequestKey();
let dragEnterCount = 0;

const rules = {
  required: (v) => !!v || "This field is required",
  npi: (v) => !v || isNPIValid(v, true) || "Enter a valid 10-digit NPI",
};

const payerRequired = (v) => !!v || !submitAttempted.value || "This field is required";

const icd10CodeRule = (v, remote) => valueInItemsAutocompleteRule(v, "Enter a valid ICD-10 code", remote);

const onSearchPayer = (value) => searchPayers(requirementsStore.clientConfig?.id, value);

const isAnyUploadInProgress = () => supplementalDocs.value.some((doc) => doc.uploading);
const documentLinks = () => supplementalDocs.value.flatMap((doc) => doc.links);

const patientDobIso = computed(() => localDateToIso(patientDobDate.value));

const networkFields = {
  patientFirstName,
  patientLastName,
  patientDobDate,
  patientGender,
  patientAddress,
  patientCity,
  patientState,
  patientZip,
};
const networkFieldErrors = computed(() =>
  Object.fromEntries(
    Object.entries(networkFields).map(([name, field]) => [
      name,
      networkFieldsRequired.value && !field.value ? [NETWORK_FIELD_MESSAGE] : [],
    ])
  )
);
const networkFieldsMissing = computed(() => Object.values(networkFields).some((field) => !field.value));

const demographicsPayload = () => ({
  patient_gender: patientGender.value || null,
  patient_address: patientAddress.value?.trim() || null,
  patient_city: patientCity.value?.trim() || null,
  patient_state: patientState.value || null,
  patient_zip: patientZip.value?.trim() || null,
});

const identityPayload = () => {
  const payload = {
    patient_first_name: patientFirstName.value.trim(),
    patient_last_name: patientLastName.value.trim(),
    patient_dob: patientDobIso.value,
  };
  const phone = phoneNumber.value?.trim();
  if (phone) payload.phone_number = phone;
  return payload;
};

function onNetworkStateChanged({ inFlight, recordCount }) {
  networkSearchInFlight.value = inFlight;
  networkRecordsFound.value = recordCount;
}

const identityKey = computed(() =>
  JSON.stringify([
    patientFirstName.value,
    patientLastName.value,
    patientDobIso.value,
    phoneNumber.value,
    patientGender.value,
    patientAddress.value,
    patientCity.value,
    patientState.value,
    patientZip.value,
  ])
);

const startNetworkQuery = async () => {
  const searchedIdentityKey = identityKey.value;
  networkSearchIdentityKey.value = searchedIdentityKey;
  const response = await intakeService.postQuickStartNetworkQuery({ ...identityPayload(), ...demographicsPayload() });
  if (identityKey.value !== searchedIdentityKey) throw new Error(NETWORK_IDENTITY_CHANGED);
  networkPatientId.value = response.patientId;
  return { run: response.run };
};

const resetNetworkSearch = () => {
  networkRecordsVisible.value = false;
  networkPatientId.value = null;
  networkSearchIdentityKey.value = null;
  networkSelectedIds.value = [];
  networkSearchInFlight.value = false;
  networkRecordsFound.value = 0;
  networkSessionKey.value += 1;
};

watch(networkPatientChosen, (chosen) => {
  if (chosen) patientMrn.value = "";
});

watch(identityKey, (key) => {
  if (networkSearchIdentityKey.value !== null && key !== networkSearchIdentityKey.value) resetNetworkSearch();
});

const onSearchNetworkRecords = async () => {
  errors.value = [];
  if (networkRecordsFound.value || networkSearchInFlight.value) {
    networkRecordsVisible.value = true;
    return;
  }
  networkFieldsRequired.value = true;
  if (networkFieldsMissing.value) {
    errors.value = [NETWORK_FIELDS_ALERT];
    return;
  }
  networkSearching.value = true;
  try {
    await networkRecordsRef.value?.startSearch();
  } finally {
    networkSearching.value = false;
  }
};

const onDragEnter = (index) => {
  dragEnterCount++;
  dragOverIndex.value = index;
};

const onDragLeave = () => {
  dragEnterCount--;
  if (dragEnterCount <= 0) {
    dragEnterCount = 0;
    dragOverIndex.value = null;
  }
};

const maxDocumentBytes = (file) => (needsServerConversion(file, file.name) ? MAX_IMAGE_SIZE_BYTES : MAX_PDF_SIZE_BYTES);

const processFile = async (index, file) => {
  const doc = supplementalDocs.value[index];
  doc.error = null;
  if (!file) return;
  doc.uploading = true;
  try {
    const link = await uploadDocumentFile(file, maxDocumentBytes);
    doc.links = [link];
    doc.label = file.name;
    doc.error = null;
  } catch (error) {
    doc.error = parseUploadError(error);
  } finally {
    doc.uploading = false;
  }
};

const handleFileDrop = async (index, event) => {
  dragOverIndex.value = null;
  dragEnterCount = 0;
  const doc = supplementalDocs.value[index];
  if (doc.links.length) {
    doc.error = "This document already has a file. Remove it first to upload a new one.";
    return;
  }
  const file = event.dataTransfer?.files?.[0];
  if (file) await processFile(index, file);
};

const handleFileSelect = async (index, event) => {
  const file = event.target.files[0];
  await processFile(index, file);
  event.target.value = "";
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  errors.value = [];
  submitAttempted.value = true;

  const { valid } = await formRef.value.validate();
  if (!valid) return;

  if (isAnyUploadInProgress()) {
    errors.value = ["Please wait for file uploads to complete before continuing."];
    return;
  }

  if (!configLoaded.value) {
    loadConfig();
    errors.value = ["The page settings are still loading. Try again in a moment."];
    return;
  }

  if (documentLinks().length === 0 && networkSelectedIds.value.length === 0) {
    errors.value = ["At least one document is required."];
    return;
  }

  errors.value = [];
  successMessage.value = null;
  patientNotes.value = [];
  isSubmitting.value = true;
  try {
    const orderId = manualEntryStart.value ? await submitManualEntryStart() : await submitQuickStart();
    if (!orderId) {
      if (errors.value.length === 0) {
        errors.value = [
          "Medical necessity review was started but no order was returned. Please check the intake grid.",
        ];
      }
      return;
    }
    createdOrderId.value = orderId;
    successMessage.value = "Medical necessity review started.";
  } catch (error) {
    if (error?.response?.data?.error_code === "request_key_reused") requestKey.reset();
    errors.value = manualEntryStart.value ? manualEntryStartErrors(error) : [];
    if (errors.value.length === 0) errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
  } finally {
    isSubmitting.value = false;
  }
};

const submitQuickStart = async () => {
  const payload = {
    ...identityPayload(),
    referral_status: referralStatus.value,
    provider_npi: providerNpi.value.trim(),
    drug_name: drugName.value.trim(),
    diagnosis_icd10_code: diagnosisIcd10Code.value.trim(),
    document_links: documentLinks(),
  };
  const payor = payorName.value?.trim();
  if (payor) payload.payor_name = payor;
  if (carequalityEnabled.value) {
    Object.assign(payload, demographicsPayload(), { network_document_ids: networkSelectedIds.value });
  }
  const response = await intakeService.postQuickStartClinicalReview(payload);
  return response?.id;
};

const manualEntryStartBody = () => {
  const mrn = patientMrn.value?.trim();
  const chosen = networkPatientChosen.value;
  return {
    step: "mn",
    patient: {
      ...(chosen ? { id: networkPatientId.value } : { ...(mrn ? { mrn } : {}) }),
      firstName: patientFirstName.value.trim(),
      lastName: patientLastName.value.trim(),
      dob: patientDobIso.value,
    },
    coverage: {
      payerNameId: Number(payerNameId.value),
      benefitType: benefitType.value,
      memberId: memberId.value.trim(),
    },
    order: {
      drugName: drugName.value.trim(),
      diagnosisIcd10Code: diagnosisIcd10Code.value.trim(),
      providerNpi: providerNpi.value.trim(),
      referralStatus: referralStatus.value,
      ...(carequalityEnabled.value ? demographicsPayload() : { patient_gender: patientGender.value }),
    },
    documents: { links: documentLinks(), networkDocumentIds: chosen ? networkSelectedIds.value : [] },
  };
};

const submitManualEntryStart = async () => {
  const body = manualEntryStartBody();
  const response = await manualEntryStartService.startMn(requestKey.for(body), body);
  if (documentsMissing(body, response)) {
    errors.value = [DOCUMENTS_NOT_FILED];
    return null;
  }
  patientNotes.value = startNotes(response, "review");
  return response.intakeOrderId;
};

const resetForm = () => {
  patientFirstName.value = "";
  patientLastName.value = "";
  patientDobDate.value = null;
  referralStatus.value = null;
  providerNpi.value = "";
  drugName.value = "";
  diagnosisIcd10Code.value = "";
  phoneNumber.value = "";
  payorName.value = "";
  patientGender.value = null;
  patientAddress.value = "";
  patientCity.value = "";
  patientState.value = null;
  patientZip.value = "";
  patientMrn.value = "";
  payerNameId.value = "";
  benefitType.value = null;
  memberId.value = "";
  patientNotes.value = [];
  submitAttempted.value = false;
  requestKey.reset();
  networkFieldsRequired.value = false;
  resetNetworkSearch();
  supplementalDocs.value = [{ label: null, links: [], file: null, uploading: false, error: null }];
  errors.value = [];
  successMessage.value = null;
  createdOrderId.value = null;
  dragOverIndex.value = null;
  dragEnterCount = 0;
  formRef.value?.resetValidation();
};

const viewOrder = () => {
  router.push({ name: "document-review-order", params: { id: createdOrderId.value } });
};
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.doc-drop-zone {
  border: 1px solid $lh_dash_border;
  background: transparent;
  transition: border $lh_transition_fast, background $lh_transition_fast;

  &--active {
    border: 2px dashed $lh_color_blue;
    background: rgba($lh_color_blue, 0.04);
  }
}

@keyframes network-badge-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.network-badge--running :deep(.v-badge__badge) {
  animation: network-badge-pulse 1.2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .network-badge--running :deep(.v-badge__badge) {
    animation: none;
  }
}
</style>
